import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import Button from "@material-ui/core/Button";
import Typography from "../../FlexiTypography";
import Box from "@material-ui/core/Box";
import BaseField from "./BaseField";
import FlexiDocumentPreview from "../../FlexiDocumentPreview";
import useFetch from "../../utils/useFetch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import DescriptionIcon from "@material-ui/icons/Description";
import GetAppIcon from "@material-ui/icons/GetApp";
import Alert from "@material-ui/lab/Alert";
import IconButton from "@material-ui/core/IconButton";
import dnload from "downloadjs";
import { DeleteConfirmation } from "../../FlexiConfirm";
import { SvgIcon } from "@material-ui/core";

export const useAlertClasses = makeStyles(() => ({
  root: {
    padding: "6px 16px"
  },
  standardInfo: {
    backgroundColor: "#F3F0F6 !important",
    "& div:nth-child(2)": {
      width: "100%"
    },
    "& svg": {
      color: "#D4C6E0"
    }
  },
  standardSuccess: {
    backgroundColor: "#F1F9E8 !important",
    "& svg ": {
      color: "#c3e29f"
    }
  },
  message: {
    "& > *": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    overflow: "hidden"
  }
}));

const thumbsContainer = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 16
};

const thumb = {
  display: "inline-flex",
  borderRadius: 2,
  border: "1px solid #eaeaea",
  marginBottom: 8,
  marginRight: 8,
  width: 100,
  height: 100,
  padding: 4,
  boxSizing: "border-box"
};

const thumbInner = {
  display: "flex",
  minWidth: 0,
  overflow: "hidden"
};

const img = {
  display: "block",
  width: "auto",
  height: "100%"
};

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 1,
  borderColor: "#eeeeee",
  backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23858585' stroke-width='2' stroke-dasharray='10' stroke-dashoffset='33' stroke-linecap='square'/%3e%3c/svg%3e")`,
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "background-image .24s ease-in-out"
};

const activeStyle = {
  borderColor: "#2196f3"
};

const acceptStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2300e676' stroke-width='3' stroke-dasharray='15' stroke-dashoffset='33' stroke-linecap='square'/%3e%3c/svg%3e")`
};

const rejectStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23ff1744' stroke-width='3' stroke-dasharray='15' stroke-dashoffset='33' stroke-linecap='square'/%3e%3c/svg%3e")`
};

const RenderFile = ({
  uploadUrl,
  placeholderText,
  uploadButtonText,
  deleteButtonText = "Törlés",
  placeholderImage,
  type,
  maxFiles,
  preview,
  accept,
  getFormData = file => {
    const formData = new FormData();
    formData.append("file", file);
    return formData;
  },
  onUploadError,
  input,
  meta,
  onUploadSuccess,
  getImageUrl,
  getMimeType = doc => doc.mimeType,
  immediateUpload = true,
  onFileChange,
  getDownloadUrl,
  getDeleteUrl,
  onDeleteFile,
  onError = console.error
}) => {
  // const { token } = useContext(FlexiFormContext);
  const token = localStorage.getItem("token");
  const alertClasses = useAlertClasses();
  const fetch = useFetch({ token });
  const initial = useMemo(
    () =>
      meta.initial && !Array.isArray(meta.initial)
        ? [meta.initial]
        : meta.initial,
    [meta.initial]
  );
  const [previewFiles, setPreviewFiles] = useState(
    (initial || []).map(item => ({
      name: item.name || item.originalName || item.contentUrl,
      id: item.id,
      mimeType: item.mimeType,
      path: item.path
    }))
  );
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(false);
  const previewFile = useMemo(() => {
    if (preview && previewFiles.length && previewFiles[0]) {
      return previewFiles[0];
    }
  }, [preview, previewFiles]);
  const deleteFile = useCallback(
    fileIndex => {
      const file = previewFiles[fileIndex];

      const asyncFunc = async () => {
        try {
          let response;
          if (getDeleteUrl && file.id) {
            const url = getDeleteUrl(file);
            response = await fetch(url, { method: "DELETE" });
          }

          const newValue = previewFiles.filter(
            (file, index) => index !== fileIndex
          );

          setPreviewFiles(newValue);
          if (immediateUpload) {
            input.onChange(newValue.map(item => item.id));
          } else {
            input.onChange(newValue);
          }
          if (onFileChange) {
            onFileChange(newValue);
          }

          return response;
        } catch (err) {
          onError(err);
        }
      };

      if (!onDeleteFile) {
        asyncFunc();
      } else {
        onDeleteFile(file, asyncFunc);
      }
    },
    [input, previewFiles, fetch]
  );

  const downloadFile = useCallback(
    file => {
      const asyncFunc = async () => {
        try {
          const url = getDownloadUrl(file);
          const data = await fetch(url, {});
          // todo image-re getter fn
          dnload(
            `data:${file.mimeType};base64,${data.image}`,
            file.name,
            file.mimeType
          );
        } catch (err) {
          onError(err);
        }
      };

      asyncFunc().catch(console.log);
    },
    [input, previewFiles]
  );

  useEffect(() => {
    if (initial) {
      input.onChange(initial.map(doc => doc.id));
      setPreviewFiles(
        initial.map(item => ({
          name: item.name || item.originalName || item.contentUrl,
          id: item.id,
          mimeType: item.mimeType,
          path: item.path
        }))
      );
      if (preview && initial.length && getImageUrl) {
        const item = initial[0];
        if (item) {
          const url = getImageUrl(item);
          setDownloading(true);
          fetch(url)
            .then(data => {
              setUploadedImage({
                image: data.image,
                mimeType: getMimeType(item)
              });
            })
            .catch(onUploadError)
            .finally(() => setDownloading(false));
        }
      }
    }
  }, [initial, preview]);

  const {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    accept,
    noClick: true,
    maxFiles: preview ? 1 : maxFiles,
    onDrop: acceptedFiles => {
      if (immediateUpload) {
        setUploading(true);
        const uploadPromises = acceptedFiles.map(file =>
          fetch(uploadUrl, { method: "POST", body: getFormData(file) })
        );

        Promise.all(uploadPromises)
          .then(data => {
            if (onUploadSuccess) {
              onUploadSuccess(data);
            }
            input.onChange(data.map(item => item.id));

            if (preview) {
              setPreviewFiles(files => [...files, ...acceptedFiles]);
            }
            setPreviewFiles(files => [
              ...files,
              ...data.map(item => ({
                name: item.originalName || item.contentUrl,
                id: item.id,
                mimeType: item.mimeType
              }))
            ]);
          })
          .catch(onUploadError)
          .finally(() => setUploading(false));
      } else {
        input.onChange([...previewFiles, ...acceptedFiles]);
        setPreviewFiles(files => [...files, ...acceptedFiles]);
      }

      if (onFileChange) {
        onFileChange(acceptedFiles);
      }
    }
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  useEffect(
    () => () => {
      previewFiles.forEach(file => URL.revokeObjectURL(file.preview));
    },
    [previewFiles]
  );

  const thumbs = previewFiles.map(file => (
    <div style={thumb} key={file.name}>
      <div style={thumbInner}>
        <img alt={file.preview} src={file.preview} style={img} />
      </div>
    </div>
  ));

  return (
    <div className="container">
      <div>
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          {preview && (
            <>
              {!previewFile && !uploadedImage && (
                <>
                  {placeholderImage && (
                    <Box p={1}>
                      {!uploading && (
                        <img
                          src={placeholderImage}
                          alt="drag and drop upload"
                        />
                      )}
                      {uploading && <CircularProgress />}
                    </Box>
                  )}
                  {placeholderText && (
                    <Box p={1}>
                      <Typography>{placeholderText}</Typography>
                    </Box>
                  )}
                </>
              )}
              {previewFile && !downloading && !uploadedImage && (
                <>
                  <FlexiDocumentPreview file={previewFile} />
                </>
              )}
              {uploadedImage && <FlexiDocumentPreview base64={uploadedImage} />}
            </>
          )}

          {!preview && (
            <>
              {placeholderImage && (
                <Box p={1}>
                  <img src={placeholderImage} alt="drag and drop upload" />
                </Box>
              )}
              {placeholderText && (
                <Box p={1}>
                  <Typography align={"center"}>{placeholderText}</Typography>
                </Box>
              )}
            </>
          )}

          {uploadButtonText && (
            <Box p={2}>
              {!previewFile && !uploadedImage && (
                <Button variant={"outlined"} onClick={open}>
                  {uploadButtonText}
                </Button>
              )}
              {(previewFile || uploadedImage) && (
                <DeleteConfirmation>
                  {({ confirm }) => (
                    <Button
                      onClick={() =>
                        confirm(() => {
                          setUploadedImage(null);
                          setPreviewFiles([]);
                          input.onChange();
                        })
                      }
                      variant={"outlined"}
                    >
                      {deleteButtonText}
                    </Button>
                  )}
                </DeleteConfirmation>
              )}
            </Box>
          )}
        </div>

        {!preview && (
          <Grid container spacing={1} style={{ marginTop: "1rem" }}>
            {uploading && <CircularProgress />}
            {previewFiles.map((file, index) => (
              <Grid item xs={12}>
                <Alert
                  classes={alertClasses}
                  severity={file.id ? "success" : "info"}
                  icon={<DescriptionIcon fontSize="inherit" />}
                  action={
                    <Grid container wrap={"nowrap"}>
                      {file.id && getDownloadUrl && (
                        <Grid item style={{ paddingRight: 10 }}>
                          <IconButton onClick={() => downloadFile(file)}>
                            <GetAppIcon />
                          </IconButton>
                        </Grid>
                      )}
                      {Boolean(
                        (file.id && (getDeleteUrl || onDeleteFile)) || !file.id
                      ) && (
                        <Grid item>
                          <DeleteConfirmation>
                            {({ confirm }) => (
                              <IconButton
                                onClick={() => confirm(() => deleteFile(index))}
                              >
                                <SvgIcon>
                                  <path
                                    d="M19.3617 3.49091H14.8936V2.4C14.8936 1.76348 14.647 1.15303 14.2081 0.702944C13.7692 0.252856 13.1739 0 12.5532 0H7.44681C6.82609 0 6.23079 0.252856 5.79188 0.702944C5.35296 1.15303 5.10638 1.76348 5.10638 2.4V3.49091H0.638298C0.469011 3.49091 0.306657 3.55987 0.186953 3.68262C0.067249 3.80537 0 3.97186 0 4.14545C0 4.31905 0.067249 4.48554 0.186953 4.60829C0.306657 4.73104 0.469011 4.8 0.638298 4.8H1.08936L2.42553 21.792C2.47638 22.3917 2.74357 22.9504 3.17485 23.3587C3.60612 23.7671 4.17045 23.9957 4.75745 24H15.2426C15.8295 23.9957 16.3939 23.7671 16.8252 23.3587C17.2564 22.9504 17.5236 22.3917 17.5745 21.792L18.9106 4.8H19.3617C19.531 4.8 19.6933 4.73104 19.813 4.60829C19.9328 4.48554 20 4.31905 20 4.14545C20 3.97186 19.9328 3.80537 19.813 3.68262C19.6933 3.55987 19.531 3.49091 19.3617 3.49091ZM6.38298 2.4C6.38298 2.11067 6.49506 1.8332 6.69457 1.62861C6.89407 1.42403 7.16466 1.30909 7.44681 1.30909H12.5532C12.8353 1.30909 13.1059 1.42403 13.3054 1.62861C13.5049 1.8332 13.617 2.11067 13.617 2.4V3.49091H6.38298V2.4ZM16.2979 21.6873C16.2745 21.9605 16.1525 22.2149 15.9557 22.4005C15.7588 22.5862 15.5015 22.6898 15.234 22.6909H4.75745C4.49149 22.6876 4.23621 22.5831 4.04119 22.3976C3.84617 22.2122 3.72533 21.959 3.70213 21.6873L2.34043 4.8H17.6596L16.2979 21.6873ZM14.0426 9.38182V18.1091C14.0426 18.2827 13.9753 18.4492 13.8556 18.5719C13.7359 18.6947 13.5735 18.7636 13.4043 18.7636C13.235 18.7636 13.0726 18.6947 12.9529 18.5719C12.8332 18.4492 12.766 18.2827 12.766 18.1091V9.38182C12.766 9.20822 12.8332 9.04174 12.9529 8.91899C13.0726 8.79623 13.235 8.72727 13.4043 8.72727C13.5735 8.72727 13.7359 8.79623 13.8556 8.91899C13.9753 9.04174 14.0426 9.20822 14.0426 9.38182ZM10.6383 9.38182V18.1091C10.6383 18.2827 10.571 18.4492 10.4513 18.5719C10.3316 18.6947 10.1693 18.7636 10 18.7636C9.83071 18.7636 9.66836 18.6947 9.54866 18.5719C9.42895 18.4492 9.3617 18.2827 9.3617 18.1091V9.38182C9.3617 9.20822 9.42895 9.04174 9.54866 8.91899C9.66836 8.79623 9.83071 8.72727 10 8.72727C10.1693 8.72727 10.3316 8.79623 10.4513 8.91899C10.571 9.04174 10.6383 9.20822 10.6383 9.38182ZM7.23404 9.38182V18.1091C7.23404 18.2827 7.16679 18.4492 7.04709 18.5719C6.92739 18.6947 6.76503 18.7636 6.59574 18.7636C6.42646 18.7636 6.2641 18.6947 6.1444 18.5719C6.0247 18.4492 5.95745 18.2827 5.95745 18.1091V9.38182C5.95745 9.20822 6.0247 9.04174 6.1444 8.91899C6.2641 8.79623 6.42646 8.72727 6.59574 8.72727C6.76503 8.72727 6.92739 8.79623 7.04709 8.91899C7.16679 9.04174 7.23404 9.20822 7.23404 9.38182Z"
                                    fill="#2227AC"
                                  />
                                </SvgIcon>
                              </IconButton>
                            )}
                          </DeleteConfirmation>
                        </Grid>
                      )}
                    </Grid>
                  }
                >
                  <Typography>{file.name}</Typography>
                </Alert>
              </Grid>
            ))}
          </Grid>
        )}

        {previewFiles && type === "preview" && (
          <aside style={thumbsContainer}>{thumbs}</aside>
        )}
      </div>
    </div>
  );
};

const FileUpload = props => {
  return <BaseField {...props} component={RenderFile} />;
};

export default FileUpload;
